import { useEffect, useRef, useState } from 'react';

interface Props {
  /** valor final */
  to: number;
  /** valor inicial (default 0) */
  from?: number;
  /** duração em ms (default 800) */
  duration?: number;
  /** formatter customizado (default Intl pt-BR) */
  format?: (n: number) => string;
  /** decimais (default 0) */
  decimals?: number;
  /** força reduced motion (default lê prefers-reduced-motion) */
  reducedMotion?: boolean;
}

const easeOutExpo = (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

const fmtPtBR = (n: number, decimals: number) =>
  n.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

/**
 * Anima contagem de número de `from` até `to` em `duration` ms.
 * Respeita prefers-reduced-motion: pula direto pro valor final.
 */
export function CountUp({ to, from = 0, duration = 800, format, decimals = 0, reducedMotion }: Props) {
  const [valor, setValor] = useState(reducedMotion ? to : from);
  const startRef = useRef<number | null>(null);
  const fromRef = useRef(from);
  const toRef = useRef(to);

  useEffect(() => {
    const reduce =
      reducedMotion ?? (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    if (reduce) {
      setValor(to);
      return;
    }
    fromRef.current = valor; // anima a partir do valor atualmente exibido
    toRef.current = to;
    startRef.current = null;
    let raf = 0;
    const tick = (ts: number) => {
      if (startRef.current == null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutExpo(t);
      const v = fromRef.current + (toRef.current - fromRef.current) * eased;
      setValor(v);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, duration, reducedMotion]);

  return <>{format ? format(valor) : fmtPtBR(valor, decimals)}</>;
}

export default CountUp;
